/**
 * Status History Component
 *
 * Displays a timeline of expense report status changes
 * showing who made each change and when.
 */

import { renderIcon, type IconName } from '../utils/icons';
import type { ExpenseReportStatusHistoryEntry, ExpenseReportStatus } from '../types/expense-tracker';
import { formatExpenseReportStatus, EXPENSE_REPORT_STATUS_STYLES } from '../types/expense-tracker';

/**
 * Format timestamp for display
 */
function formatTimestamp(timestamp: string): string {
  try {
    const date = new Date(timestamp);
    return date.toLocaleString('de-CH', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return timestamp;
  }
}

/**
 * Get icon for status
 */
function getStatusIcon(status: ExpenseReportStatus): IconName {
  const style = EXPENSE_REPORT_STATUS_STYLES[status];
  return (style?.icon || 'info') as IconName;
}

/**
 * Get status color class
 */
function getStatusColorClass(status: ExpenseReportStatus): string {
  const style = EXPENSE_REPORT_STATUS_STYLES[status];
  return style?.text || 'text-muted-foreground';
}

/**
 * Create a single status history entry
 */
function createHistoryEntry(
  entry: ExpenseReportStatusHistoryEntry,
  _isFirst: boolean,
  isLast: boolean
): HTMLElement {
  const item = document.createElement('div');
  item.className = 'relative flex gap-4';

  // Timeline connector line
  const connector = document.createElement('div');
  connector.className = `absolute left-3 top-6 w-0.5 ${isLast ? 'h-0' : 'h-full'} bg-border`;

  // Status icon circle
  const iconWrapper = document.createElement('div');
  const colorClass = getStatusColorClass(entry.status);
  iconWrapper.className = `relative z-10 flex-shrink-0 w-6 h-6 rounded-full bg-card border-2 border-border flex items-center justify-center ${colorClass}`;
  iconWrapper.appendChild(renderIcon(getStatusIcon(entry.status), { size: 'xs' }));

  // Content
  const content = document.createElement('div');
  content.className = 'flex-1 pb-6';

  const header = document.createElement('div');
  header.className = 'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1';

  const statusLabel = document.createElement('span');
  statusLabel.className = `font-medium ${colorClass}`;
  statusLabel.textContent = formatExpenseReportStatus(entry.status);

  const timestamp = document.createElement('span');
  timestamp.className = 'text-xs text-muted-foreground';
  timestamp.textContent = formatTimestamp(entry.timestamp);

  header.appendChild(statusLabel);
  header.appendChild(timestamp);
  content.appendChild(header);

  // User who made the change
  const userInfo = document.createElement('p');
  userInfo.className = 'text-sm text-muted-foreground mt-1';
  userInfo.textContent = entry.userName ? `Changed by ${entry.userName}` : `User ID: ${entry.userId}`;
  content.appendChild(userInfo);

  // Notes if present
  if (entry.notes) {
    const notes = document.createElement('p');
    notes.className = 'text-sm text-muted-foreground mt-1 italic';
    notes.textContent = `"${entry.notes}"`;
    content.appendChild(notes);
  }

  item.appendChild(connector);
  item.appendChild(iconWrapper);
  item.appendChild(content);

  return item;
}

/**
 * Create status history component
 */
export function createStatusHistory(history: ExpenseReportStatusHistoryEntry[]): HTMLElement {
  const container = document.createElement('div');
  container.className = 'glass-card rounded-2xl p-6';

  // Header
  const header = document.createElement('div');
  header.className = 'flex items-center gap-2 mb-6';
  header.innerHTML = `
    ${renderIcon('history', { size: 'md', className: 'text-primary' }).outerHTML}
    <h3 class="text-lg font-semibold text-foreground">Status History</h3>
  `;
  container.appendChild(header);

  // Timeline
  const timeline = document.createElement('div');
  timeline.className = 'ml-1';

  if (history.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'text-muted-foreground text-sm';
    empty.textContent = 'No status changes recorded yet.';
    timeline.appendChild(empty);
  } else {
    // Sort history by timestamp descending (most recent first)
    const sortedHistory = [...history].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );

    sortedHistory.forEach((entry, index) => {
      const isFirst = index === 0;
      const isLast = index === sortedHistory.length - 1;
      timeline.appendChild(createHistoryEntry(entry, isFirst, isLast));
    });
  }

  container.appendChild(timeline);

  return container;
}

export default createStatusHistory;
